import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Alert, Badge, Button, Spinner } from "reactstrap";

import { canConcludeAppointments, canMarkClientArrived } from "../../../../Routes/roleAccess";
import { getDisplayableAppointmentNotes } from "../../../../helpers/appointmentNotesDisplay";
import { canViewClinicalRecords } from "../../../../helpers/clinicalRecordAccess";
import {
  Appointment,
  getById,
  getDepositPix,
  isGatewayPixPendingDeposit,
  isManualPendingDeposit,
  sendDepositPix,
  type DepositPixChannel,
} from "../../../../services/appointmentService";

export type AppointmentDetailPanelProps = {
  variant?: "panel" | "embedded";
  appointment: Appointment | null;
  statusUpdatingId: number | null;
  notAvailable: string;
  resolveStatusLabel: (status: string) => string;
  resolveStatusColor: (status: string) => string;
  isActionableStatus: (status: string) => boolean;
  formatTimeRange: (start: string, end: string) => string;
  formatDateLabel: (value: string) => string;
  resolveProfessionalColor: (appointment: Appointment) => string;
  onClose: () => void;
  onConclude: (appointment: Appointment) => void;
  onNoShow: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onConfirmDeposit: (appointment: Appointment) => void;
  onEdit: (appointment: Appointment) => void;
  onMarkArrived?: (appointment: Appointment) => void;
  onClearArrived?: (appointment: Appointment) => void;
  onFillHealthRecord?: (appointment: Appointment) => void;
  onRegisterEvolution?: (appointment: Appointment) => void;
  onViewEvolution?: (appointment: Appointment) => void;
  onShowDepositPix?: (pix: {
    appointmentId: number;
    qrCodeBase64: string;
    copyPasteString: string;
  }) => void;
};

const formatSentAt = (value: string | null, locale: string): string => {
  if (!value) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const AppointmentDetailPanel = ({
  variant = "panel",
  appointment,
  statusUpdatingId,
  notAvailable,
  resolveStatusLabel,
  resolveStatusColor,
  isActionableStatus,
  formatTimeRange,
  formatDateLabel,
  resolveProfessionalColor,
  onClose,
  onConclude,
  onNoShow,
  onCancel,
  onConfirmDeposit,
  onEdit,
  onMarkArrived,
  onClearArrived,
  onFillHealthRecord,
  onRegisterEvolution,
  onViewEvolution,
  onShowDepositPix,
}: AppointmentDetailPanelProps) => {
  const { t, i18n } = useTranslation();
  const [detailAppointment, setDetailAppointment] = useState<Appointment | null>(appointment);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [sendingDepositChannel, setSendingDepositChannel] = useState<DepositPixChannel | null>(null);
  const [loadingDepositPix, setLoadingDepositPix] = useState(false);
  const allowConclude = canConcludeAppointments();
  const allowMarkArrived = canMarkClientArrived();
  const canHealthRecord = canViewClinicalRecords();
  const showNoHealthBadge =
    canHealthRecord &&
    detailAppointment != null &&
    (typeof detailAppointment.needs_health_record === "boolean"
      ? detailAppointment.needs_health_record
      : (detailAppointment.client?.health_records_count ?? 0) === 0);
  const showClinicalGateBadge =
    canHealthRecord &&
    detailAppointment != null &&
    Boolean(detailAppointment.has_clinical_gate_alert);
  const showNeedsConsentBadge =
    canHealthRecord && Boolean(detailAppointment?.needs_consent);
  const showPreattendOkBadge =
    canHealthRecord &&
    detailAppointment != null &&
    Boolean(detailAppointment.client_id) &&
    !showNoHealthBadge &&
    !showNeedsConsentBadge;
  const showPreattendPendingArrival =
    canHealthRecord &&
    Boolean(detailAppointment?.client_arrived_at) &&
    (showNoHealthBadge || showNeedsConsentBadge);
  const canShowArrivedCta =
    allowMarkArrived &&
    onMarkArrived != null &&
    detailAppointment != null &&
    ["scheduled", "confirmed"].includes(detailAppointment.status) &&
    !detailAppointment.client_arrived_at;
  const showArrivedBadge = Boolean(detailAppointment?.client_arrived_at);

  useEffect(() => {
    setDetailAppointment(appointment);

    if (!appointment) {
      return;
    }

    let cancelled = false;
    setLoadingReminders(true);

    getById(appointment.id)
      .then((response) => {
        if (!cancelled) {
          setDetailAppointment(response.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDetailAppointment(appointment);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingReminders(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appointment]);

  const showManualDepositConfirmation =
    detailAppointment !== null && isManualPendingDeposit(detailAppointment);

  const showGatewayPixDepositActions =
    detailAppointment !== null && isGatewayPixPendingDeposit(detailAppointment);

  const handleSendDepositPix = async (channel: DepositPixChannel) => {
    if (!detailAppointment || sendingDepositChannel !== null) {
      return;
    }

    setSendingDepositChannel(channel);

    try {
      const response = await sendDepositPix(detailAppointment.id, { channels: [channel] });
      const channelResult = response.data.channels[channel];

      if (channelResult?.status === "queued") {
        toast.success(
          channel === "email"
            ? t("agenda.pix.sendEmailSuccess")
            : t("agenda.pix.sendWhatsappSuccess")
        );
        return;
      }

      toast.error(channelResult?.message || t("agenda.pix.sendFailed"));
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("agenda.pix.sendFailed"));
    } finally {
      setSendingDepositChannel(null);
    }
  };

  const handleShowDepositPix = async () => {
    if (!detailAppointment || !onShowDepositPix || loadingDepositPix) {
      return;
    }

    setLoadingDepositPix(true);

    try {
      const response = await getDepositPix(detailAppointment.id);
      onShowDepositPix({
        appointmentId: detailAppointment.id,
        qrCodeBase64: response.data.pix.qr_code_base64,
        copyPasteString: response.data.pix.copy_paste_string,
      });
    } catch (error: unknown) {
      const apiError = error as { data?: string };
      toast.error(apiError?.data || t("agenda.pix.loadQrError"));
    } finally {
      setLoadingDepositPix(false);
    }
  };

  const notificationReminders = useMemo(
    () => detailAppointment?.notification_reminders ?? [],
    [detailAppointment?.notification_reminders]
  );

  const whatsAppReminders = useMemo(
    () =>
      notificationReminders
        .filter((item) => item.channel === "whatsapp")
        .sort((left, right) => {
          const leftTime = left.sent_at ? new Date(left.sent_at).getTime() : 0;
          const rightTime = right.sent_at ? new Date(right.sent_at).getTime() : 0;

          return leftTime - rightTime;
        }),
    [notificationReminders]
  );

  const resolveReminderLabel = (reminder: (typeof whatsAppReminders)[number]): string => {
    if (reminder.reminder_type === "created") {
      return t("studio.agenda.detail.notifications.typeCreated");
    }

    return t("studio.agenda.detail.notifications.typeReminder", {
      hours: reminder.hours_before ?? 0,
    });
  };

  const resolveDeliveryModeLabel = (deliveryMode: string | null): string => {
    if (deliveryMode === "tenant_number") {
      return t("studio.agenda.detail.notifications.deliveryTenantNumber");
    }

    if (deliveryMode === "pooled") {
      return t("studio.agenda.detail.notifications.deliveryPooled");
    }

    return t("studio.agenda.detail.notifications.deliveryUnknown");
  };

  return (
    <div className={variant === "panel" ? "border rounded p-3 bg-light-subtle" : "p-0"}>
      <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
        <h6 className="mb-0">{t("studio.agenda.detail.title")}</h6>
        {detailAppointment ? (
          <Button
            color="link"
            className="p-0 text-muted"
            onClick={onClose}
            aria-label={t("studio.agenda.detail.close")}
          >
            <i className="ri-close-line fs-5" />
          </Button>
        ) : null}
      </div>

      {!detailAppointment ? (
        <p className="text-muted mb-0 small">{t("studio.agenda.detail.empty")}</p>
      ) : (
        <>
          <div className="mb-3 d-flex flex-wrap gap-2">
            <Badge color={resolveStatusColor(detailAppointment.status)} className="text-uppercase">
              {resolveStatusLabel(detailAppointment.status)}
            </Badge>
            {detailAppointment.has_package_credit ? (
              <Badge color="info">
                {t("studio.agenda.badges.packageCredit", {
                  count: detailAppointment.package_credit_sessions ?? 0,
                })}
              </Badge>
            ) : null}
            {detailAppointment.has_active_club ? (
              <Badge color="primary">
                {detailAppointment.club_visits_remaining != null
                  ? t("studio.agenda.badges.clubMemberRemaining", {
                      count: detailAppointment.club_visits_remaining,
                    })
                  : t("studio.agenda.badges.clubMember")}
              </Badge>
            ) : null}
            {showNoHealthBadge ? (
              <Badge color="warning" className="text-dark">
                {t("studio.agenda.badges.noHealthRecord")}
              </Badge>
            ) : null}
            {showClinicalGateBadge ? (
              <Badge color="danger" className="text-wrap d-inline-flex align-items-center gap-1">
                <i className="ri-alert-line" aria-hidden="true" />
                {t("studio.agenda.badges.clinicalGate")}
              </Badge>
            ) : null}
            {showNeedsConsentBadge ? (
              <Badge color="danger" className="text-wrap">
                {t("studio.agenda.badges.needsConsent")}
              </Badge>
            ) : null}
            {showPreattendPendingArrival ? (
              <Badge
                color="warning"
                className="text-dark text-wrap"
                title={t("studio.agenda.badges.preattendPendingArrivalHint")}
              >
                {t("studio.agenda.badges.preattendPendingArrival")}
              </Badge>
            ) : null}
            {showPreattendOkBadge ? (
              <Badge
                color="success"
                className="text-wrap"
                title={t("studio.agenda.badges.preattendOkHint")}
              >
                {t("studio.agenda.badges.preattendOk")}
              </Badge>
            ) : null}
            {showArrivedBadge ? (
              <Badge color="success" className="d-inline-flex align-items-center gap-1">
                {t("studio.agenda.badges.arrived")}
                {allowMarkArrived && onClearArrived ? (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-white text-decoration-underline"
                    style={{ fontSize: "0.7rem", lineHeight: 1 }}
                    disabled={statusUpdatingId === detailAppointment.id}
                    onClick={() => onClearArrived(detailAppointment)}
                  >
                    {t("studio.agenda.actions.arrived.undo")}
                  </button>
                ) : null}
              </Badge>
            ) : null}
            {showManualDepositConfirmation ? (
              <Badge color="warning">
                {t("studio.agenda.deposit.confirmation.awaitingManual")}
              </Badge>
            ) : null}
          </div>

          <dl className="mb-0">
            <dt className="text-muted small mb-1">{t("studio.agenda.detail.date")}</dt>
            <dd className="mb-3">{formatDateLabel(detailAppointment.start_datetime.slice(0, 10))}</dd>

            <dt className="text-muted small mb-1">{t("studio.agenda.detail.time")}</dt>
            <dd className="mb-3">
              {formatTimeRange(detailAppointment.start_datetime, detailAppointment.end_datetime)}
            </dd>

            <dt className="text-muted small mb-1">{t("studio.agenda.detail.client")}</dt>
            <dd className="mb-3 fw-medium">{detailAppointment.client?.name ?? notAvailable}</dd>

            <dt className="text-muted small mb-1">{t("studio.agenda.detail.professional")}</dt>
            <dd className="mb-3 d-flex align-items-center gap-2">
              <span
                className="rounded-circle flex-shrink-0"
                style={{
                  width: "0.625rem",
                  height: "0.625rem",
                  backgroundColor: resolveProfessionalColor(detailAppointment),
                }}
                aria-hidden="true"
              />
              {detailAppointment.professional?.name ?? notAvailable}
            </dd>

            <dt className="text-muted small mb-1">{t("studio.agenda.detail.service")}</dt>
            <dd className="mb-3">{detailAppointment.service?.name ?? notAvailable}</dd>

            {(detailAppointment.extra_services ?? []).length > 0 ? (
              <>
                <dt className="text-muted small mb-1">
                  {t("studio.agenda.detail.extraServices")}
                </dt>
                <dd className="mb-3">
                  <ul className="mb-0 ps-3">
                    {[...(detailAppointment.extra_services ?? [])]
                      .sort(
                        (left, right) =>
                          (left.sort_order ?? 0) - (right.sort_order ?? 0)
                      )
                      .map((extra, index) => (
                        <li key={extra.id}>
                          {t("studio.agenda.detail.extraServicesOrdered", {
                            position: index + 1,
                            name: extra.name ?? `#${extra.id}`,
                          })}
                        </li>
                      ))}
                  </ul>
                </dd>
                <Alert color="light" className="py-2 px-3 mb-3 border">
                  <small className="mb-0">
                    {t("studio.agenda.detail.clientFacingPreview", {
                      label: [
                        detailAppointment.service?.name,
                        ...(detailAppointment.extra_services ?? [])
                          .map((extra) => extra.name)
                          .filter(Boolean),
                      ]
                        .filter(Boolean)
                        .join(" + "),
                    })}
                  </small>
                </Alert>
              </>
            ) : null}

            {getDisplayableAppointmentNotes(detailAppointment.notes) ? (
              <>
                <dt className="text-muted small mb-1">{t("studio.agenda.detail.notes")}</dt>
                <dd className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
                  {getDisplayableAppointmentNotes(detailAppointment.notes)}
                </dd>
              </>
            ) : null}

            <dt className="text-muted small mb-1">{t("studio.agenda.detail.unit")}</dt>
            <dd className="mb-0">{detailAppointment.unit?.name ?? notAvailable}</dd>
          </dl>

          <div className="mt-4 pt-3 border-top">
            <h6 className="mb-2">{t("studio.agenda.detail.notifications.title")}</h6>
            {loadingReminders ? (
              <div className="d-flex align-items-center gap-2 text-muted small">
                <Spinner size="sm" />
                {t("studio.agenda.detail.notifications.loading")}
              </div>
            ) : whatsAppReminders.length === 0 ? (
              <Alert color="light" className="py-2 px-3 mb-0 border">
                <small className="mb-0">{t("studio.agenda.detail.notifications.empty")}</small>
              </Alert>
            ) : (
              <ul className="list-unstyled mb-0 small">
                {whatsAppReminders.map((reminder) => (
                  <li
                    key={reminder.id}
                    className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom"
                  >
                    <div>
                      <span className="fw-medium">{resolveReminderLabel(reminder)}</span>
                      <span className="text-muted ms-2">
                        {resolveDeliveryModeLabel(reminder.delivery_mode)}
                      </span>
                    </div>
                    <span className="text-muted">
                      {formatSentAt(reminder.sent_at, i18n.language)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {showManualDepositConfirmation ? (
            <div className="mt-4 pt-3 border-top">
              <Button
                color="success"
                size="sm"
                className="w-100"
                disabled={statusUpdatingId === detailAppointment.id}
                onClick={() => onConfirmDeposit(detailAppointment)}
              >
                {statusUpdatingId === detailAppointment.id ? (
                  <Spinner size="sm" className="me-1" />
                ) : null}
                {t("studio.agenda.deposit.confirmation.confirmReceived")}
              </Button>
            </div>
          ) : null}

          {showGatewayPixDepositActions ? (
            <div className="mt-4 pt-3 border-top">
              <p className="text-muted small mb-2">{t("agenda.pix.detailSendHint")}</p>
              <div className="d-flex flex-column gap-2">
                {onShowDepositPix ? (
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    disabled={loadingDepositPix || sendingDepositChannel !== null}
                    onClick={() => void handleShowDepositPix()}
                  >
                    {loadingDepositPix ? <Spinner size="sm" className="me-1" /> : (
                      <i className="ri-qr-code-line me-1" />
                    )}
                    {t("agenda.pix.showQr")}
                  </Button>
                ) : null}
                <Button
                  color="soft-success"
                  size="sm"
                  disabled={sendingDepositChannel !== null || loadingDepositPix}
                  onClick={() => void handleSendDepositPix("email")}
                >
                  {sendingDepositChannel === "email" ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-mail-send-line me-1" />
                  )}
                  {t("agenda.pix.sendEmail")}
                </Button>
                <Button
                  color="soft-success"
                  size="sm"
                  disabled={sendingDepositChannel !== null || loadingDepositPix}
                  onClick={() => void handleSendDepositPix("whatsapp")}
                >
                  {sendingDepositChannel === "whatsapp" ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-whatsapp-line me-1" />
                  )}
                  {t("agenda.pix.sendWhatsapp")}
                </Button>
              </div>
            </div>
          ) : null}

          {(showNoHealthBadge || showNeedsConsentBadge) && onFillHealthRecord ? (
            <div className="d-flex flex-column gap-2 mt-4 pt-3 border-top">
              <Button
                color={showNeedsConsentBadge && !showNoHealthBadge ? "danger" : "warning"}
                size="sm"
                outline
                onClick={() => onFillHealthRecord(detailAppointment)}
              >
                <i
                  className={
                    showNeedsConsentBadge && !showNoHealthBadge
                      ? "ri-quill-pen-line me-1"
                      : "ri-heart-add-line me-1"
                  }
                  aria-hidden="true"
                />
                {showNeedsConsentBadge && !showNoHealthBadge
                  ? t("studio.agenda.actions.fillConsent")
                  : showPreattendPendingArrival
                    ? t("studio.agenda.actions.fillPreattendPending")
                    : t("studio.agenda.actions.fillHealthRecord")}
              </Button>
            </div>
          ) : null}

          {canHealthRecord &&
          detailAppointment &&
          (detailAppointment.can_register_evolution ||
            detailAppointment.has_evolution) ? (
            <div className="d-flex flex-column gap-2 mt-4 pt-3 border-top">
              {detailAppointment.evolution_photos_expiring_soon &&
              detailAppointment.evolution_photos_expire_in_days !== null &&
              detailAppointment.evolution_photos_expire_in_days !== undefined ? (
                <Alert color="warning" className="py-2 mb-0">
                  <div className="d-flex flex-column gap-2">
                    <span className="small mb-0">
                      {detailAppointment.evolution_photos_expire_in_days === 0
                        ? t("studio.agenda.evolutionPhotos.expireToday")
                        : t("studio.agenda.evolutionPhotos.expireInDays", {
                            days: detailAppointment.evolution_photos_expire_in_days,
                          })}
                    </span>
                    {onViewEvolution && detailAppointment.has_evolution ? (
                      <Button
                        color="warning"
                        size="sm"
                        outline
                        onClick={() => onViewEvolution(detailAppointment)}
                      >
                        {t("studio.agenda.evolutionPhotos.openRecord")}
                      </Button>
                    ) : null}
                  </div>
                </Alert>
              ) : null}
              {detailAppointment.has_evolution && onViewEvolution ? (
                <Button
                  color="info"
                  size="sm"
                  outline
                  onClick={() => onViewEvolution(detailAppointment)}
                >
                  <i className="ri-file-list-3-line me-1" aria-hidden="true" />
                  {t("studio.agenda.actions.viewEvolution")}
                </Button>
              ) : null}
              {detailAppointment.can_register_evolution && onRegisterEvolution ? (
                <Button
                  color="primary"
                  size="sm"
                  outline
                  onClick={() => onRegisterEvolution(detailAppointment)}
                >
                  <i className="ri-camera-lens-line me-1" aria-hidden="true" />
                  {t("studio.agenda.actions.registerEvolution")}
                </Button>
              ) : null}
            </div>
          ) : null}

          {isActionableStatus(detailAppointment.status) ? (
            <div className="d-flex flex-column gap-2 mt-4 pt-3 border-top">
              <Button
                color="secondary"
                size="sm"
                outline
                disabled={statusUpdatingId === detailAppointment.id}
                onClick={() => onEdit(detailAppointment)}
              >
                {t("studio.agenda.actions.edit.label")}
              </Button>
              {canShowArrivedCta ? (
                <Button
                  color="info"
                  size="sm"
                  outline
                  disabled={statusUpdatingId === detailAppointment.id}
                  onClick={() => onMarkArrived?.(detailAppointment)}
                >
                  {statusUpdatingId === detailAppointment.id ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-walk-line me-1" aria-hidden="true" />
                  )}
                  {t("studio.agenda.actions.arrived.label")}
                </Button>
              ) : null}
              {allowConclude ? (
                <Button
                  color="success"
                  size="sm"
                  disabled={statusUpdatingId === detailAppointment.id}
                  onClick={() => onConclude(detailAppointment)}
                >
                  {statusUpdatingId === detailAppointment.id ? (
                    <Spinner size="sm" className="me-1" />
                  ) : null}
                  {t("studio.agenda.actions.conclude.label")}
                </Button>
              ) : null}
              <Button
                color="warning"
                size="sm"
                outline
                disabled={statusUpdatingId === detailAppointment.id}
                onClick={() => onNoShow(detailAppointment)}
              >
                {t("studio.agenda.actions.noShow.label")}
              </Button>
              <Button
                color="danger"
                size="sm"
                outline
                disabled={statusUpdatingId === detailAppointment.id}
                onClick={() => onCancel(detailAppointment)}
              >
                {t("studio.agenda.actions.cancelAppointment.label")}
              </Button>
            </div>
          ) : (
            <Alert color="light" className="mt-4 mb-0 py-2 border">
              <small className="mb-0">{t("studio.agenda.detail.inactiveStatusHint")}</small>
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

export default AppointmentDetailPanel;
